// TestsTab.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { FilterService, FilterMatchMode } from 'primereact/api';
import { ProgressBar } from 'primereact/progressbar';
import { InputText } from 'primereact/inputtext';

import {
  baseURL,
  fetchTestsData,
  handleUnauthorized,
  dateTemplate,
  fetchTestTermination,
} from './DataFlow';
import {
  binaryPathTemplate,
  allurePathTemplate,
  jenkinsUrlTemplate,
} from './Templates';

let loadingButton = null;

// Register custom filter for passRate
FilterService.register('custom_passRate', (value, filter) => {
  const [from, to] = filter ?? [null, null];
  if (from === null && to === null) return true;
  if (from !== null && to === null) return from <= value;
  if (from === null && to !== null) return value <= to;
  return from <= value && value <= to;
});

const TestsTab = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [testsData, setTestsData] = useState([]);
  const [testsFirst, setTestsFirst] = useState(0);
  const [testsRows, setTestsRows] = useState(5);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [terminationDialogVisible, setTerminationDialogVisible] =
    useState(false);
  const [testToTerminate, setTestToTerminate] = useState(null);
  const [powerUser, setPowerUser] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [allureLoading, setAllureLoading] = useState(false);

  // note
  const [noteDialogVisible, setNoteDialogVisible] = useState(false);
  const [noteBeingEdited, setNoteBeingEdited] = useState('');
  const [testForNoteEdit, setTestForNoteEdit] = useState(null);
  const [noteSaving, setNoteSaving] = useState(false);

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    passRate: { value: null, matchMode: FilterMatchMode.CUSTOM },
    appName: { value: null, matchMode: FilterMatchMode.EQUALS },
    origin: { value: null, matchMode: FilterMatchMode.EQUALS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    platform: { value: null, matchMode: FilterMatchMode.EQUALS },
    version: { value: null, matchMode: FilterMatchMode.CONTAINS }, // Loose filter for version
  });


  const toast = useRef(null);

  useEffect(() => {
    if (!hasFetched) {
      fetchTestsData(setTestsData, handleUnauthorized, setPowerUser);
      setHasFetched(true);
    }
  }, [powerUser, hasFetched]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    if (id && testsData.length > 0) {
      const test = testsData.find((t) => t._id === id);
      if (test) {
        setSelectedRowData(test);
      }
    }
  }, [location.search, testsData]);

  const onTestsPageChange = (e) => {
    setTestsFirst(e.first);
    setTestsRows(e.rows);
  };

  const onHide = () => {
    setSelectedTest(null);
  };

  const onDialogHide = () => {
    setSelectedRowData(null);
    // Remove the id parameter from the URL
    const params = new URLSearchParams(location.search);
    params.delete('id');
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  const rowClassNameForTests = (node) => {
    return {
      'row-passed':
        node.status === 'Completed' && Number(node.passRate) >= 25,
      'row-warning':
        node.status === 'InProgress' || node.status === 'Scheduled',
      'row-failed':
        Number(node.passRate) < 25 && node.status === 'Completed',
      'row-terminated': node.status === 'Terminated',
    };
  };

  const clientOptions = [
    { label: 'Cashman', value: 'CC' },
    { label: 'Lightning Link', value: 'LL' },
    { label: 'Heart of Vegas', value: 'HOV' },
    { label: 'Mighty Fu Casino', value: 'MFC' },
  ];

  const clientFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={clientOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select a Client"
        className="p-column-filter"
        showClear
      />
    );
  };

  const originOptions = [
    { label: 'manual', value: 'manual' },
    { label: 'internal', value: 'internal' },
    { label: 'trigger', value: 'trigger' },
    { label: 'schedule', value: 'schedule' },
  ];

  const originFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={originOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select an Origin"
        className="p-column-filter"
        showClear
      />
    );
  };

  const versionFilterTemplate = (options) => {
    return (
      <InputText
        value={options.value}
        onChange={(e) => options.filterApplyCallback(e.target.value)}
        placeholder="Search Version"
        className="p-column-filter"
      />
    );
  };

  const platformOptions = [
    { label: 'iOS', value: 'ios' },
    { label: 'Android', value: 'android' },
  ];

  const platformFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={platformOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select Platform"
        className="p-column-filter"
        showClear
      />
    );
  };

  const statusOptions = [
    { label: 'In Progress', value: 'InProgress' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Scheduled', value: 'Scheduled' },
    { label: 'Failed', value: 'Failed' }, // something went horribly wrong
    { label: 'Terminated', value: 'Terminated' },
  ];

  const statusFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder="Select a Status"
        className="p-column-filter"
        showClear
      />
    );
  };

  const passRateFilterTemplate = (options) => {
    const [from, to] = options.value || [null, null];

    return (
      <div className="flex gap-1">
        <InputNumber
          value={from}
          onChange={(e) => options.filterApplyCallback([e.value, to])}
          placeholder="Min"
        />
        <InputNumber
          value={to}
          onChange={(e) => options.filterApplyCallback([from, e.value])}
          placeholder="Max"
        />
      </div>
    );
  };

  const formatLiveScore = (node) => {
    if (node.origin === 'manual') {
      return null; // Return blank if trigger is 'manual'
    }

    return (
      <div
        style={{
          backgroundColor: '#2c2c2c',
          borderRadius: '4px',
          padding: '3px 4px',
          display: 'inline-block',
          width:'100%'
        }}
        title={`Passed - Green
Failed - Red
Skipped - Orange
Broken - Purple`}
      > 
       <table
        style={{
          fontSize: 'small',
          color: 'white',
          borderCollapse: 'collapse', // Removes space between cells
          lineHeight: '1', // Reduces the space between lines
          width:'100%'
        }}
      >
        <tbody>
          <tr>
            <td
              style={{
                color: 'limegreen',
                padding: '2px 4px', // Adjust padding as needed
              }}
            >
              {n(node.livePassed)}
            </td>
            <td
              style={{
                color: 'tomato',
                padding: '2px 4px',
              }}
            >
              {n(node.liveFailed)}
            </td>
          </tr>
          <tr>
            <td
              style={{
                color: 'gold',
                padding: '2px 4px',
              }}
            >
              {n(node.liveSkipped)}
            </td>
            <td
              style={{
                color: 'plum',
                padding: '2px 4px',
              }}
            >
              {n(node.liveBroken)}
            </td>
          </tr>
          {node.liveScoreMaxCount > 0 && (
            <tr>
              <td
                style={{
                  color: 'white',
                  padding: '2px 4px',
                }}
              >
                all
              </td>
              <td
                style={{
                  color: 'white',
                  padding: '2px 4px',
                }}
              >
                {n(node.liveScoreMaxCount)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
       {node.liveScoreMaxCount > 0 && (
        <ProgressBar 
        style={{ height: '13px', width: '100%', marginTop:'5px', borderRadius:'2px', fontSize:"8pt" }}
        value={
          Math.min(100,Math.round((
          (Number(node.liveFailed)+
           Number(node.livePassed)+
           Number(node.liveSkipped)+
           Number(node.liveBroken)) / node.liveScoreMaxCount)*100
          ))} />
      )}
      </div >
    );
  };

  function n(value) {
    if (value === undefined || value === null || isNaN(value)) {
      return 0;
    }
    return value;
  }

  function getAllureLinkVisibility(rowData) {
    if (rowData.status !== 'Completed') {
      return { visibility: 'hidden', display: 'none' };
    } else {
      return { visibility: 'visible', display: 'block' };
    }
  }

  const formatPassRate = (rowData) => {
    if (rowData.passRate !== undefined && rowData.passRate !== null) {
      return Number(rowData.passRate).toFixed(2) + ' %';
    }

    return '%';
  };

  const handleTabChange = (tab, rowData) => {
    console.log(rowData);
    const id = rowData._id;
    navigate(`/results?id=${id}`);
  };

  const showResultsDialog = (e, rowData) => {
    loadingButton = e.currentTarget;

    e.currentTarget.querySelector(':nth-child(1)').style.visibility = 'hidden';
    e.currentTarget.querySelector(':nth-child(1)').style.display = 'none';
    e.currentTarget.querySelector(':nth-child(2)').style.visibility = 'visible';
    e.currentTarget.querySelector(':nth-child(2)').style.display = 'block';

    FetchGoogleBucketAllureReport(rowData);
  };

  const FetchGoogleBucketAllureReport = async (rowData) => {
    try {
      console.time();
      const authToken = localStorage.getItem('apiToken');

      const response = await fetch(baseURL + '/api/tests/allure-helper', {
        headers: {
          api_token: authToken,
          user_type: 'OktaUser',
          buildId: rowData._id,
          allureArchive: rowData.allureArchive,
        },
      });
      const data = await response.json();
      if (typeof data === 'object') {
        console.log(data);
        // TODO display errors
      } else {
        setSelectedTest(data);
      }

      if (loadingButton) {
        loadingButton.querySelector(':nth-child(2)').style.visibility =
          'hidden';
        loadingButton.querySelector(':nth-child(2)').style.display = 'none';
        loadingButton.querySelector(':nth-child(1)').style.visibility =
          'visible';
        loadingButton.querySelector(':nth-child(1)').style.display = 'block';
      }

      loadingButton = null;

      console.timeEnd();
    } catch (error) {
      console.error('Error fetching agents data:', error);
    } finally {
      setAllureLoading(false);
    }
  };

  // Function to render the "Terminate Test" and "Copy Link" buttons
  const renderActions = (rowData) => {
    const isOngoing =
      rowData.status === 'InProgress' ||
      rowData.status === 'Scheduled' ||
      rowData.status === 'Init';

    return (
      <React.Fragment>
        {powerUser && isOngoing && (
          <Button
            onClick={() => confirmTermination(rowData)}
            icon="pi pi-trash"
            className="p-button-rounded p-button-danger p-button-text white-icon-button"
            tooltip="Terminate Test"
            tooltipOptions={{ position: 'top' }}
          />
        )}
        <Button
          onClick={() => copyLink(rowData)}
          icon="pi pi-link"
          className="p-button-rounded p-button-text white-icon-button"
          tooltip="Copy Link"
          tooltipOptions={{ position: 'top' }}
        />
        {powerUser && (
          <Button
            onClick={() => openNoteDialog(rowData)}
            icon="pi pi-pencil"
            className="p-button-rounded p-button-danger p-button-text white-icon-button"
            tooltip="Edit Note"
            tooltipOptions={{ position: 'top' }}
          />
        )}
      </React.Fragment>
    );
  };

  const copyLink = (rowData) => {
    const link = `${window.location.origin}/tests?id=${rowData._id}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.current.show({
          severity: 'success',
          summary: 'Link Copied',
          detail: 'Test link copied to clipboard',
          life: 3000,
        });
      })
      .catch((err) => {
        console.error('Could not copy text: ', err);
      });
  };

  const openNoteDialog = (rowData) => {
    setTestForNoteEdit(rowData);
    setNoteBeingEdited(rowData.note || '');
    setNoteDialogVisible(true);
  };

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      const authToken = localStorage.getItem('apiToken');

      const response = await fetch(`${baseURL}/api/tests/${testForNoteEdit._id}/note`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          api_token: authToken,
          user_type: 'OktaUser',
        },
        body: JSON.stringify({ note: noteBeingEdited }),
      });

      if (response.ok) {
        // Update the note in the local state
        setTestsData((prevTestsData) =>
          prevTestsData.map((test) =>
            test._id === testForNoteEdit._id ? { ...test, note: noteBeingEdited } : test
          )
        );

        // If the test is currently selected, update it
        if (selectedRowData && selectedRowData._id === testForNoteEdit._id) {
          setSelectedRowData({ ...selectedRowData, note: noteBeingEdited });
        }

        toast.current.show({
          severity: 'success',
          summary: 'Note Saved',
          detail: 'The note has been saved successfully.',
          life: 3000,
        });
      } else {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save the note.',
          life: 3000,
        });
      }
    } catch (error) {
      console.error('Error saving note:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'An error occurred while saving the note.',
        life: 3000,
      });
    } finally {
      setNoteSaving(false);
      setNoteDialogVisible(false);
    }
  };

  const testNameFormatter = (rowData) => {
    if (rowData.testSuite === undefined || rowData.testSuite === null) {
      return "none";
    }
    var split = rowData.testSuite.split('=');

    if (split.length > 0) {
      return split[1];
    }

    return rowData.testSuite;
  };

  const confirmTermination = (rowData) => {
    setTestToTerminate(rowData);
    setTerminationDialogVisible(true);
  };

  const handleTermination = () => {
    terminateTest(testToTerminate);
    setTerminationDialogVisible(false);
  };

  const terminateTest = async (test) => {
    var result = await fetchTestTermination(test);

    if (result.success === true || result.success === 'true') {
      setTestsData((prevTestsData) =>
        prevTestsData.map((t) =>
          t._id === test._id ? { ...t, status: 'Terminated' } : t
        )
      );
      // Display success toast
      toast.current.show({
        severity: 'success',
        summary: 'Terminated',
        detail: 'Test successfully terminated!',
        life: 10000,
      });
    } else {
      // Display error toast
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not terminate test execution on the agent!',
        life: 10000,
      });
    }
  };

  const dialogFooter = (
    <div style={{ textAlign: 'center', margin: 'auto' }}>
      <Button
        onClick={handleTermination}
        className="p-button p-component p-button-danger"
        style={{ margin: '15px' }}
      >
        Yes
      </Button>
      <Button
        onClick={() => setTerminationDialogVisible(false)}
        className="p-button p-component"
        style={{ margin: '15px' }}
      >
        No
      </Button>
    </div>
  );

  const handleRowClick = (e) => {
    const rowData = e.data;
    setSelectedRowData(rowData);

    // Update the URL with the build id
    const id = rowData._id;
    navigate(`/tests?id=${id}`);
  };

  // Function to open Allure Report from the popup
  const openAllureReport = async (rowData) => {
    setAllureLoading(true);
    await FetchGoogleBucketAllureReport(rowData);
  };

  // Function to format date fields
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      <Toast ref={toast} />
      <DataTable
        value={testsData}
        first={testsFirst}
        rowClassName={rowClassNameForTests}
        rows={testsRows}
        onPage={onTestsPageChange}
        paginator
        rowsPerPageOptions={[5, 10, 20]}
        sortField="createdAt"
        sortOrder={-1}
        onRowClick={handleRowClick}
        showGridlines
        filterDisplay="menu"
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        globalFilterFields={['appName', 'platform', 'version', 'status', 'testSuite']}
      >
        <Column
          body={(rowData) => (
            <button
              style={getAllureLinkVisibility(rowData)}
              className="icon-button"
              severity="info"
              onClick={() => handleTabChange('search', rowData)}
            >
              <img
                alt="logo"
                src="./logo/results.svg"
                className="allure-icon"
              ></img>
            </button>
          )}
        />
        {/* <Column field="buildNumber" header="BN-AAD" />
        <Column field="buildNumberRunner" header="BN-Runner" /> */}
        <Column field="appName" header="App Name" filter filterElement={clientFilterTemplate} />
        <Column field="platform" header="Platform" filter filterElement={platformFilterTemplate} />
        <Column field="version" header="Version" filter filterElement={versionFilterTemplate} />
        <Column field="status" header="Status" sortable filter filterElement={statusFilterTemplate} />
        <Column field="testSuite" header="Test" body={testNameFormatter} />
        <Column field="priority" header="Priority" sortable />
        <Column field="origin" header="Origin" filter filterElement={originFilterTemplate} />
        <Column field="lobbyBranch" header="Lobby Branch" />
        <Column
          field="createdAt"
          header="Created"
          sortable
          body={dateTemplate}
        />
        {/* <Column field="binaryPath" header="Binary" body={binaryPathTemplate} /> */}
        <Column header="Jenkins URL" body={jenkinsUrlTemplate} />
        <Column
          field="passRate"
          header="Pass Rate"
          body={formatPassRate}
          sortable
          filter
          filterMatchMode="custom"
          filterFunction={(value, filter) => {
            const [from, to] = filter ?? [null, null];
            if (from === null && to === null) return true;
            if (from !== null && to === null) return from <= value;
            if (from === null && to !== null) return value <= to;
            return from <= value && value <= to;
          }}
          filterElement={passRateFilterTemplate}
        />
        {/* <Column header="Live" body={formatLiveScore} /> */}
        {/* <Column
          field="allureArchive"
          header="Allure"
          body={allurePathTemplate}
        /> */}
        <Column header="Allure Report"
          body={(rowData) => (
            <button
              onClick={(e) => showResultsDialog(e, rowData)}
              style={getAllureLinkVisibility(rowData)}
              className="icon-button"
            >
              <img
                alt="logo"
                src="./logo/allure.svg"
                className="allure-icon"
              ></img>
              <div
                className="card"
                style={{ visibility: 'hidden', display: 'none' }}
              >
                <ProgressSpinner
                  style={{ width: '25px', height: '25px' }}
                  strokeWidth="8"
                  fill="var(--surface-ground)"
                  animationDuration="1s"
                />
              </div>
            </button>
          )}
        />
        <Column header="Actions" body={renderActions} />
      </DataTable>

      {/* Dialog for displaying test details */}
      <Dialog
        header="Test Details"
        visible={selectedRowData !== null}
        onHide={onDialogHide}
        style={{ width: '50%' }}
      >
        {selectedRowData && (
          <table className="test-details-table">
            <tbody>
              <tr>
                <td>
                  <strong>Build Number</strong>
                </td>
                <td>{selectedRowData.buildNumber}</td>
              </tr>
              <tr>
                <td>
                  <strong>Build Number Runner</strong>
                </td>
                <td>{selectedRowData.buildNumberRunner}</td>
              </tr>
              <tr>
                <td>
                  <strong>App Name</strong>
                </td>
                <td>{selectedRowData.appName}</td>
              </tr>
              <tr>
                <td>
                  <strong>Status</strong>
                </td>
                <td>{selectedRowData.status}</td>
              </tr>
              <tr>
                <td>
                  <strong>Platform</strong>
                </td>
                <td>{selectedRowData.platform}</td>
              </tr>
              <tr>
                <td>
                  <strong>Version</strong>
                </td>
                <td>{selectedRowData.version}</td>
              </tr>
              <tr>
                <td>
                  <strong>Test Suite</strong>
                </td>
                <td>{selectedRowData.testSuite}</td>
              </tr>
              <tr>
                <td>
                  <strong>Priority</strong>
                </td>
                <td>{selectedRowData.priority}</td>
              </tr>
              <tr>
                <td>
                  <strong>Origin</strong>
                </td>
                <td>{selectedRowData.origin}</td>
              </tr>
              <tr>
                <td>
                  <strong>Agent</strong>
                </td>
                <td>{selectedRowData.agentName? selectedRowData.agentName : "-"}</td>
              </tr>
              <tr>
                <td>
                  <strong>Lobby Branch</strong>
                </td>
                <td>{selectedRowData.lobbyBranch}</td>
              </tr>
              <tr>
                <td>
                  <strong>Created At</strong>
                </td>
                <td>{formatDate(selectedRowData.createdAt)}</td>
              </tr>
              <tr>
                <td>
                  <strong>Binary</strong>
                </td>
                <td>
                  {binaryPathTemplate(selectedRowData)}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Jenkins URL</strong>
                </td>
                <td>
                  {jenkinsUrlTemplate(selectedRowData)}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Pass Rate</strong>
                </td>
                <td>{formatPassRate(selectedRowData)}</td>
              </tr>
              <tr>
                <td>
                  <strong>Live Score Last Update</strong>
                </td>
                <td>{formatDate(selectedRowData.liveScoreLastUpdate)}</td>
              </tr>
              <tr>
                <td>
                  <strong>Allure Archive</strong>
                </td>
                <td>
                  {allurePathTemplate(selectedRowData)}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Note</strong>
                </td>
                <td>{selectedRowData.note || 'No notes'}</td>
              </tr>
              <tr>
                <td>
                  <strong>Allure Report</strong>
                </td>
                <td>
                  {getAllureLinkVisibility(selectedRowData).visibility ===
                    'visible' ? (
                    <Button
                      onClick={() => openAllureReport(selectedRowData)}
                      className="p-button-text"
                      disabled={allureLoading}
                    >
                      {allureLoading ? (
                        <ProgressSpinner
                          style={{ width: '25px', height: '25px' }}
                          strokeWidth="8"
                          fill="var(--surface-ground)"
                          animationDuration="1s"
                        />
                      ) : (
                        'View Report'
                      )}
                    </Button>
                  ) : (
                    'Not Available'
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </Dialog>

      {/* Existing Dialog for Allure Report */}
      <Dialog
        maximizable
        style={{ width: '75vw', height: '75vh' }}
        visible={!!selectedTest}
        onHide={onHide}
      >
        <iframe
          title="Allure report"
          id="allure-frame"
          src={`${baseURL}${selectedTest}?token=${localStorage.getItem(
            'apiToken'
          )}`}
          width="100%"
          height="100%"
        />
      </Dialog>

      {/* Confirmation Dialog for Termination */}
      <Dialog
        header="Confirm Termination"
        visible={terminationDialogVisible}
        style={{ width: '50%' }}
        modal
        onHide={() => setTerminationDialogVisible(false)}
        footer={dialogFooter}
      >
        <p>Are you sure you want to terminate this test run?</p>
        {testToTerminate && (
          <table className="test-details-table">
            <tbody>
              <tr>
                <td>
                  <strong>Build Number</strong>
                </td>
                <td>{testToTerminate.buildNumber}</td>
              </tr>
              <tr>
                <td>
                  <strong>App Name</strong>
                </td>
                <td>{testToTerminate.appName}</td>
              </tr>
              <tr>
                <td>
                  <strong>Status</strong>
                </td>
                <td>{testToTerminate.status}</td>
              </tr>
              <tr>
                <td>
                  <strong>Platform</strong>
                </td>
                <td>{testToTerminate.platform}</td>
              </tr>
              <tr>
                <td>
                  <strong>Version</strong>
                </td>
                <td>{testToTerminate.version}</td>
              </tr>
              <tr>
                <td>
                  <strong>Test Suite</strong>
                </td>
                <td>{testToTerminate.testSuite}</td>
              </tr>
              <tr>
                <td>
                  <strong>Priority</strong>
                </td>
                <td>{testToTerminate.priority}</td>
              </tr>
              {/* Include other properties as needed */}
            </tbody>
          </table>
        )}
      </Dialog>

      {/* Dialog for Editing Note */}
      <Dialog
        header="Edit Note"
        visible={noteDialogVisible}
        onHide={() => setNoteDialogVisible(false)}
        style={{ width: '40%' }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setNoteDialogVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={saveNote}
              loading={noteSaving}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="note">Note</label>
            <InputTextarea
              id="note"
              value={noteBeingEdited}
              onChange={(e) => setNoteBeingEdited(e.target.value)}
              rows={5}
              cols={30}
              autoResize
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default TestsTab;
